import React from "react";

export interface TabItem<T extends string = string> {
  id: T;
  label: string;
  /** Omit on tabs that carry no count badge, e.g. the brand setup screen. */
  count?: number;
}

interface TabsProps<T extends string> {
  tabs: TabItem<T>[];
  activeTab: T;
  onChange: (tab: T) => void;
}

// Generic over the tab id so each screen keeps its own union of tab names
// instead of widening to `string`.
function Tabs<T extends string>({ tabs, activeTab, onChange }: TabsProps<T>) {
  return (
    <div role="tablist" className="flex items-center gap-6">
      {tabs.map((tab) => {
        const active = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.id)}
            className={`relative flex items-center gap-2 pb-3 text-[14px] 3xl:text-[16px] transition-colors ${
              active ? "text-[#00BACB] font-medium" : "text-ink"
            }`}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span
                className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-medium text-white ${
                  active ? "bg-[#00BACB]" : "bg-[#00424B]"
                }`}
              >
                {tab.count}
              </span>
            )}
            {/* Sits on top of the parent's bottom border rather than replacing
                it, so switching tabs doesn't shift the row by a pixel. */}
            {active && (
              <span
                aria-hidden="true"
                className="absolute inset-x-0 -bottom-px h-[2px] rounded-full bg-brand"
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

export default Tabs;
